import { useState } from "react";
import { Box, Typography, TextField, Button, InputAdornment, IconButton } from "@mui/material";
import { styles } from "./stylesConnectWithEmail";
import { useNavigate } from "react-router-dom";
import LogoBlueWalletModatSvg from "@/assets/svg/LogoBlueWalletModatSvg";
import { usePasswordResetConfirmMutation } from "@/store/api/walletApi";
import EyeSvg from "@/assets/svg/EyeSvg";
type Props = {
  token: string;
};

const SetNewPasswordModal = ({ token }: Props) => {
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [error, setError] = useState("");
  const [resetPassword] = usePasswordResetConfirmMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    if (!password || !repeatPassword) return "Fields are required";
    if (password !== repeatPassword) return "Passwords do not match";
    if (!/(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/.test(password))
      return "Password must be at least 8 characters, include a number and a special character";
    return "";
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) return setError(validationError);

    try {
      if (!token) {
        setError("Invalid or missing token");
        return;
      }
      const res = await resetPassword({
        token,
        password,
        passwordconf: password,
      }).unwrap();
      if (res.message?.toLowerCase().includes("success")) {
        navigate("/");
      } else {
        setError("Failed to reset password");
      }
    } catch {
      setError("Something went wrong");
    }
  };

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.content}>
        <Box sx={styles.logoBox}>
          <LogoBlueWalletModatSvg />
        </Box>

        <Typography sx={styles.title}>Set a new password</Typography>
        <Typography sx={styles.subtitle}>Enter and confirm your new password</Typography>

        <Box sx={styles.inputWrapper}>
          <Typography sx={styles.label}>New password</Typography>
          <TextField
            variant="outlined"
            placeholder="Enter your password"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={styles.input}
            InputProps={{
              sx: styles.inputText,
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    marginRight: "12px",
                  }}
                >
                  <IconButton
                    edge="end"
                    disableRipple
                    disableFocusRipple
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{
                      padding: 0,
                      backgroundColor: "transparent",
                      marginRight: "-25px",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    <EyeSvg
                      style={{
                        width: 24,
                        height: 24,
                        color: showPassword ? "#383C40" : "#C6CACE",
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={styles.inputWrapper}>
          <Typography sx={styles.label}>Repeat new password</Typography>
          <TextField
            variant="outlined"
            placeholder="Repeat your password"
            type={showRepeatPassword ? "text" : "password"}
            fullWidth
            value={repeatPassword}
            onChange={(e) => setRepeatPassword(e.target.value)}
            sx={styles.input}
            InputProps={{
              sx: styles.inputText,
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    marginRight: "12px",
                  }}
                >
                  <IconButton
                    edge="end"
                    disableRipple
                    disableFocusRipple
                    onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                    sx={{
                      padding: 0,
                      backgroundColor: "transparent",
                      marginRight: "-25px",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    <EyeSvg
                      style={{
                        width: 24,
                        height: 24,
                        color: showRepeatPassword ? "#383C40" : "#C6CACE",
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {error && (
          <Typography variant="h6" color="error" textAlign="center" mt={2}>
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          sx={styles.button}
          onClick={handleSubmit}
          disabled={!password || !repeatPassword}
        >
          Set password
        </Button>
      </Box>
    </Box>
  );
};

export default SetNewPasswordModal;
